import { useEffect, useRef, useState, useCallback } from "react";
import { Send, Smile, Mic, MicOff, Pin, Reply, Search, X, Paperclip, Sparkles, FileText, Pencil, Trash2, Check } from "lucide-react";
import { api, fileToBase64 } from "@/lib/api";
import { useSession } from "@/App";
import { toast } from "sonner";

const EMOJIS = ["👍", "❤️", "😂", "🎉", "😮", "🙏"];

const formatTime = (iso) => new Date(iso).toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" });

export default function FamilyChatPage() {
  const { user } = useSession();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [replyTo, setReplyTo] = useState(null);
  const [showSearch, setShowSearch] = useState(false);
  const [searchQ, setSearchQ] = useState("");
  const [showEmojiFor, setShowEmojiFor] = useState(null);
  const [recording, setRecording] = useState(false);
  const [showPinned, setShowPinned] = useState(false);
  const [transcribing, setTranscribing] = useState(null);
  const [transcripts, setTranscripts] = useState({});
  const [suggestions, setSuggestions] = useState({});  // {sug_id: suggestion_object}
  const [actingOnSug, setActingOnSug] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [editingText, setEditingText] = useState("");
  const recorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const fileRef = useRef(null);
  const scrollRef = useRef(null);

  const fetchMessages = useCallback(async () => {
    try {
      const { data } = await api.get("/family-chat");
      setMessages(data);
    } catch (err) {
      if (process.env.NODE_ENV !== "production") console.error("Failed to fetch chat messages:", err?.message);
    }
  }, []);

  const fetchSuggestions = useCallback(async () => {
    try {
      const { data } = await api.get("/memo/suggestions");
      const map = {};
      (data || []).forEach((s) => { map[s.id] = s; });
      setSuggestions(map);
    } catch (err) {
      if (process.env.NODE_ENV !== "production") console.error("Failed to fetch suggestions:", err?.message);
    }
  }, []);

  const approveSuggestion = async (sid) => {
    setActingOnSug(sid);
    try {
      const { data } = await api.post(`/memo/suggestions/${sid}/approve`);
      setSuggestions((prev) => ({ ...prev, [sid]: { ...(prev[sid] || {}), status: "approved" } }));
      window.dispatchEvent(new Event("memo:suggestion-changed"));
      toast.success(`Aggiunto: ${data?.created?.name || data?.created?.title || "Memo AI"}`);
    } catch (err) {
      if (process.env.NODE_ENV !== "production") console.error("Approve suggestion failed:", err);
      toast.error(err?.response?.data?.detail || "Errore");
    } finally {
      setActingOnSug(null);
    }
  };

  const rejectSuggestion = async (sid) => {
    setActingOnSug(sid);
    try {
      await api.post(`/memo/suggestions/${sid}/reject`);
      setSuggestions((prev) => ({ ...prev, [sid]: { ...(prev[sid] || {}), status: "rejected" } }));
      window.dispatchEvent(new Event("memo:suggestion-changed"));
      toast.info("Ignorato");
    } catch (err) {
      if (process.env.NODE_ENV !== "production") console.error("Reject suggestion failed:", err);
      toast.error("Errore");
    } finally {
      setActingOnSug(null);
    }
  };

  const startEdit = (m) => {
    setEditingId(m.id);
    setEditingText(m.text || "");
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditingText("");
  };

  const saveEdit = async () => {
    const text = editingText.trim();
    if (!text || !editingId) return cancelEdit();
    try {
      const { data } = await api.put(`/family-chat/${editingId}`, { text });
      setMessages((prev) => prev.map((m) => (m.id === editingId ? { ...m, text: data.text, edited_at: data.edited_at } : m)));
      cancelEdit();
      toast.success("Messaggio modificato");
    } catch (err) {
      if (process.env.NODE_ENV !== "production") console.error("Edit failed:", err);
      toast.error(err?.response?.data?.detail || "Errore modifica");
    }
  };

  const deleteMessage = async (m) => {
    if (!window.confirm("Eliminare questo messaggio?")) return;
    try {
      await api.delete(`/family-chat/${m.id}`);
      setMessages((prev) => prev.filter((x) => x.id !== m.id));
      toast.success("Messaggio eliminato");
    } catch (err) {
      if (process.env.NODE_ENV !== "production") console.error("Delete failed:", err);
      toast.error(err?.response?.data?.detail || "Errore eliminazione");
    }
  };

  useEffect(() => {
    fetchMessages();
    fetchSuggestions();
    const id = setInterval(() => { fetchMessages(); fetchSuggestions(); }, 4000);
    return () => clearInterval(id);
  }, [fetchMessages, fetchSuggestions]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages.length]);

  const send = async (extra = {}) => {
    const text = input.trim();
    if (!text && !extra.attachment_b64) return;
    setSending(true);
    try {
      const { data } = await api.post("/family-chat", {
        text: text || (extra.attachment_type === "image" ? "📷 Foto" : extra.attachment_type === "audio" ? "🎤 Vocale" : "📎 File"),
        reply_to: replyTo?.id || null,
        ...extra,
      });
      setInput("");
      setReplyTo(null);
      if (data.ai_actions?.length) {
        const titles = data.ai_actions.map((a) => (typeof a === "string" ? a : a.title)).filter(Boolean);
        if (titles.length) toast.success(`AI: ${titles.join(" · ")}`);
        window.dispatchEvent(new Event("memo:suggestion-changed"));
      }
      fetchMessages();
      fetchSuggestions();
    } catch {
      toast.error("Errore invio");
    } finally {
      setSending(false);
    }
  };

  const onFile = async (e, type = "image") => {
    const file = e.target.files?.[0];
    if (!file) return;
    const b64 = await fileToBase64(file);
    send({ attachment_b64: b64, attachment_type: type, attachment_name: file.name });
  };

  const startRec = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const rec = new MediaRecorder(stream);
      audioChunksRef.current = [];
      rec.ondataavailable = (e) => audioChunksRef.current.push(e.data);
      rec.onstop = async () => {
        const blob = new Blob(audioChunksRef.current, { type: "audio/webm" });
        const reader = new FileReader();
        reader.onload = () => {
          const b64 = reader.result.split(",")[1];
          send({ attachment_b64: b64, attachment_type: "audio", attachment_name: "voice.webm" });
        };
        reader.readAsDataURL(blob);
        stream.getTracks().forEach(t => t.stop());
      };
      rec.start();
      recorderRef.current = rec;
      setRecording(true);
    } catch {
      toast.error("Microfono non disponibile");
    }
  };

  const stopRec = () => {
    recorderRef.current?.stop();
    setRecording(false);
  };

  const react = async (msg_id, emoji) => {
    await api.post("/family-chat/react", { message_id: msg_id, emoji });
    setShowEmojiFor(null);
    fetchMessages();
  };

  const pin = async (id) => {
    await api.post(`/family-chat/${id}/pin`);
    fetchMessages();
  };

  const transcribe = async (msg) => {
    if (transcripts[msg.id]) return;
    setTranscribing(msg.id);
    try {
      const { data } = await api.post("/voice/transcribe", {
        audio_b64: msg.attachment_b64,
        mime_type: "audio/webm",
        language: "it",
      });
      setTranscripts({ ...transcripts, [msg.id]: data.text });
    } catch (e) {
      if (e?.response?.status === 402) {
        toast.error("Trascrizione vocali disponibile dal piano Standard");
      } else {
        toast.error("Trascrizione fallita");
      }
    } finally {
      setTranscribing(null);
    }
  };

  const filtered = searchQ
    ? messages.filter(m => m.text?.toLowerCase().includes(searchQ.toLowerCase()))
    : messages;
  const pinnedMessages = messages.filter(m => m.pinned);

  return (
    <div className="flex flex-col h-screen">
      <div className="px-5 md:px-10 py-4 border-b border-subtle bg-glass sticky top-0 z-10">
        <div className="flex items-center justify-between max-w-4xl mx-auto gap-3">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-full bg-sage grid place-items-center text-white font-semibold">F</div>
            <div>
              <div className="font-display font-medium">Chat Famiglia</div>
              <div className="text-xs text-tertiary-fg">L&apos;AI ascolta e organizza</div>
            </div>
          </div>
          <div className="flex gap-1">
            {pinnedMessages.length > 0 && (
              <button onClick={() => setShowPinned(!showPinned)} data-testid="chat-pinned-toggle" className="p-2 rounded-full hover:bg-elevated">
                <Pin className="w-4 h-4 text-amber-warm" />
              </button>
            )}
            <button onClick={() => setShowSearch(!showSearch)} data-testid="chat-search-toggle" className="p-2 rounded-full hover:bg-elevated">
              <Search className="w-4 h-4" />
            </button>
          </div>
        </div>
        {showSearch && (
          <div className="max-w-4xl mx-auto mt-3">
            <input
              data-testid="chat-search-input"
              autoFocus
              className="input-warm"
              placeholder="Cerca nei messaggi..."
              value={searchQ}
              onChange={(e) => setSearchQ(e.target.value)}
            />
          </div>
        )}
        {showPinned && pinnedMessages.length > 0 && (
          <div className="max-w-4xl mx-auto mt-3 space-y-1">
            {pinnedMessages.map(p => (
              <div key={p.id} className="bg-elevated rounded-xl px-3 py-2 text-xs flex items-start gap-2">
                <Pin className="w-3 h-3 text-amber-warm mt-0.5" />
                <div><span className="font-medium">{p.user_name}:</span> {p.text}</div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div ref={scrollRef} className="flex-1 overflow-auto px-3 md:px-10 py-4 pb-32 bg-app">
        <div className="max-w-3xl mx-auto space-y-2">
          {filtered.map((m) => {
            const mine = m.user_id === user.id;
            const isAI = m.user_id === "memo-ai" || m.user_id === "ai-system";
            const repliedMsg = m.reply_to ? messages.find(x => x.id === m.reply_to) : null;
            const memoSugIds = (m.memo_suggestions || []).filter(sid => suggestions[sid]);
            return (
              <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"} group`}>
                {!mine && !isAI && (
                  <div className="w-8 h-8 rounded-full grid place-items-center text-white text-xs font-semibold mr-2 mt-1 shrink-0"
                       style={{ backgroundColor: m.avatar_color || "#E07A5F" }}>
                    {m.user_name?.[0]?.toUpperCase()}
                  </div>
                )}
                {isAI && (
                  <div className="w-8 h-8 rounded-full bg-gradient-to-br from-sage to-info grid place-items-center mr-2 mt-1 shrink-0">
                    <Sparkles className="w-4 h-4 text-white" strokeWidth={1.8} />
                  </div>
                )}
                <div className="max-w-[78%]">
                  {!mine && !isAI && <div className="text-xs text-tertiary-fg mb-0.5 ml-1">{m.user_name}</div>}
                  {isAI && <div className="text-xs text-sage mb-0.5 ml-1 font-medium">Memo AI</div>}
                  {repliedMsg && (
                    <div className="bg-elevated/50 border-l-2 border-brand rounded-lg px-2 py-1 mb-1 text-xs text-tertiary-fg truncate">
                      ↪ <span className="text-secondary-fg">{repliedMsg.user_name}</span>: {repliedMsg.text?.slice(0, 60)}
                    </div>
                  )}
                  <div className={`relative rounded-2xl px-3 py-2 ${
                    mine ? "bg-brand text-white rounded-tr-sm"
                      : isAI ? "bg-elevated border border-sage/40 text-primary-fg"
                      : "bg-surface border border-subtle text-primary-fg rounded-tl-sm"
                  }`}>
                    {m.attachment_b64 && m.attachment_type === "image" && (
                      <img src={`data:image/jpeg;base64,${m.attachment_b64}`} className="rounded-xl max-h-64 mb-2" alt="" />
                    )}
                    {m.attachment_b64 && m.attachment_type === "audio" && (
                      <div className="mb-2">
                        <audio controls src={`data:audio/webm;base64,${m.attachment_b64}`} className="w-full" />
                        <button
                          onClick={() => transcribe(m)}
                          disabled={transcribing === m.id}
                          data-testid={`transcribe-button-${m.id}`}
                          className="text-xs mt-1 flex items-center gap-1 text-tertiary-fg hover:text-brand transition-colors"
                        >
                          <FileText className="w-3 h-3" />
                          {transcribing === m.id ? "Trascrivendo..." : transcripts[m.id] ? "Trascrizione:" : "Trascrivi (AI)"}
                        </button>
                        {transcripts[m.id] && (
                          <div className="text-xs italic text-secondary-fg mt-1 px-2 py-1 bg-elevated rounded-lg">
                            &ldquo;{transcripts[m.id]}&rdquo;
                          </div>
                        )}
                      </div>
                    )}
                    {m.attachment_b64 && m.attachment_type === "document" && (
                      <a href={`data:application/octet-stream;base64,${m.attachment_b64}`} download={m.attachment_name}
                         className="text-sm underline">📎 {m.attachment_name}</a>
                    )}
                    {editingId === m.id ? (
                      <div className="space-y-1.5">
                        <textarea
                          data-testid={`chat-edit-input-${m.id}`}
                          autoFocus
                          value={editingText}
                          onChange={(e) => setEditingText(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); saveEdit(); }
                            if (e.key === "Escape") cancelEdit();
                          }}
                          rows={Math.min(6, Math.max(1, (editingText.match(/\n/g) || []).length + 1))}
                          className="w-full bg-app/40 border border-subtle rounded-xl px-2 py-1.5 text-sm text-primary-fg resize-none focus:outline-none focus:border-brand"
                        />
                        <div className="flex gap-1.5">
                          <button
                            onClick={saveEdit}
                            data-testid={`chat-edit-save-${m.id}`}
                            className="flex items-center gap-1 bg-brand text-white rounded-full px-3 py-1 text-xs font-medium hover:bg-[var(--brand-hover)]"
                          >
                            <Check className="w-3 h-3" /> Salva
                          </button>
                          <button
                            onClick={cancelEdit}
                            className="bg-elevated text-tertiary-fg rounded-full px-3 py-1 text-xs hover:bg-subtle"
                          >
                            Annulla
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="text-sm whitespace-pre-wrap break-words">{m.text}</div>
                    )}
                    {isAI && memoSugIds.length > 0 && (
                      <div className="mt-2 space-y-1.5">
                        {memoSugIds.map(sid => {
                          const s = suggestions[sid];
                          if (!s) return null;
                          const isPending = s.status === "pending";
                          const isApproved = s.status === "approved";
                          const isRejected = s.status === "rejected";
                          const typeLabel = s.type === "shopping" ? "Lista spesa" : s.type === "calendar_event" ? "Calendario" : "Promemoria";
                          return (
                            <div key={sid} data-testid={`memo-suggestion-${sid}`} className="bg-app/40 border border-subtle rounded-xl p-2.5">
                              <div className="text-[10px] uppercase tracking-widest text-tertiary-fg font-semibold mb-1">{typeLabel}</div>
                              <div className="text-sm font-medium">{s.title}{s.type === "calendar_event" && s.payload?.date && <span className="text-xs text-tertiary-fg ml-2">{s.payload.date}</span>}</div>
                              {isPending && (
                                <div className="flex gap-2 mt-2">
                                  <button
                                    onClick={() => approveSuggestion(sid)}
                                    disabled={actingOnSug === sid}
                                    data-testid={`memo-approve-${sid}`}
                                    className="flex-1 bg-brand text-white rounded-full py-1.5 text-xs font-medium hover:bg-[var(--brand-hover)] disabled:opacity-50"
                                  >
                                    Aggiungi
                                  </button>
                                  <button
                                    onClick={() => rejectSuggestion(sid)}
                                    disabled={actingOnSug === sid}
                                    data-testid={`memo-reject-${sid}`}
                                    className="flex-1 bg-elevated text-tertiary-fg rounded-full py-1.5 text-xs hover:bg-subtle"
                                  >
                                    Ignora
                                  </button>
                                </div>
                              )}
                              {isApproved && (
                                <div className="text-xs text-sage mt-1.5 flex items-center gap-1">
                                  ✓ Aggiunto
                                </div>
                              )}
                              {isRejected && (
                                <div className="text-xs text-tertiary-fg mt-1.5">Ignorato</div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                    <div className={`text-[10px] mt-1 ${mine ? "text-white/70" : "text-tertiary-fg"}`}>
                      {formatTime(m.created_at)} {m.edited_at && <span className="italic opacity-75">· modificato</span>} {m.pinned && <Pin className="w-2.5 h-2.5 inline ml-1" />}
                    </div>
                    {Object.keys(m.reactions || {}).length > 0 && (
                      <div className="absolute -bottom-2 right-2 flex gap-1">
                        {Object.entries(m.reactions || {}).map(([e, users]) => (
                          <button key={e} onClick={() => react(m.id, e)} className="bg-elevated border border-subtle rounded-full px-1.5 py-0.5 text-xs">
                            {e} {users.length}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="flex gap-1 mt-1.5 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button onClick={() => setShowEmojiFor(showEmojiFor === m.id ? null : m.id)} className="p-1 rounded-full hover:bg-elevated text-tertiary-fg">
                      <Smile className="w-3.5 h-3.5" />
                    </button>
                    <button onClick={() => setReplyTo(m)} data-testid={`reply-button-${m.id}`} className="p-1 rounded-full hover:bg-elevated text-tertiary-fg">
                      <Reply className="w-3.5 h-3.5" />
                    </button>
                    <button onClick={() => pin(m.id)} className="p-1 rounded-full hover:bg-elevated text-tertiary-fg">
                      <Pin className="w-3.5 h-3.5" />
                    </button>
                    {mine && !isAI && editingId !== m.id && (
                      <button
                        onClick={() => startEdit(m)}
                        data-testid={`edit-button-${m.id}`}
                        aria-label="Modifica"
                        className="p-1 rounded-full hover:bg-elevated text-tertiary-fg"
                      >
                        <Pencil className="w-3.5 h-3.5" />
                      </button>
                    )}
                    {(mine || user.role === "admin") && (
                      <button
                        onClick={() => deleteMessage(m)}
                        data-testid={`delete-button-${m.id}`}
                        aria-label="Elimina"
                        className="p-1 rounded-full hover:bg-elevated text-tertiary-fg hover:text-red-400"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>
                  {showEmojiFor === m.id && (
                    <div className="bg-surface border border-subtle rounded-full px-2 py-1 mt-1 flex gap-1 w-fit">
                      {EMOJIS.map(e => (
                        <button key={e} onClick={() => react(m.id, e)} className="hover:bg-elevated rounded-full px-1 text-base">{e}</button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
          {messages.length === 0 && (
            <div className="text-center py-12 text-tertiary-fg text-sm">Nessun messaggio. Inizia la conversazione!</div>
          )}
        </div>
      </div>

      <div className="fixed bottom-0 left-0 right-0 md:left-60 lg:left-64 bg-glass border-t border-subtle px-3 md:px-10 py-3 pb-20 md:pb-3 z-20">
        <div className="max-w-3xl mx-auto">
          {replyTo && (
            <div className="flex items-center gap-2 mb-2 bg-elevated rounded-xl px-3 py-1.5 text-xs">
              <Reply className="w-3 h-3 text-brand" />
              <span className="text-tertiary-fg">Rispondi a {replyTo.user_name}: {replyTo.text?.slice(0, 50)}</span>
              <button onClick={() => setReplyTo(null)} className="ml-auto"><X className="w-3 h-3" /></button>
            </div>
          )}
          <div className="flex gap-2 items-center">
            <button onClick={() => fileRef.current?.click()} data-testid="chat-attach-button" className="p-2.5 rounded-full hover:bg-elevated text-secondary-fg">
              <Paperclip className="w-4 h-4" />
            </button>
            <input
              ref={fileRef}
              type="file"
              accept="image/*,application/pdf"
              hidden
              onChange={(e) => onFile(e, e.target.files[0]?.type?.startsWith("image/") ? "image" : "document")}
            />
            <input
              data-testid="chat-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && send()}
              placeholder="Scrivi un messaggio..."
              className="input-warm flex-1 rounded-full"
            />
            {!input.trim() && (
              <button
                onClick={recording ? stopRec : startRec}
                data-testid="chat-mic-button"
                className={`p-2.5 rounded-full ${recording ? "bg-rose-warm text-white" : "hover:bg-elevated text-secondary-fg"}`}
              >
                {recording ? <MicOff className="w-4 h-4" /> : <Mic className="w-4 h-4" />}
              </button>
            )}
            {input.trim() && (
              <button
                onClick={() => send()}
                disabled={sending}
                data-testid="chat-send-button"
                className="btn-primary !px-4 !py-2.5"
              >
                <Send className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

